import { useCallback, useEffect } from "react";
import { atom, useRecoilState, useRecoilValue, useSetRecoilState } from "recoil";
import { Alert, AlertProps, Popover, PopoverProps } from "@mui/material";

type TProps = {
  message: string;
  severity?: AlertProps["severity"];
  anchor?: HTMLElement;
  anchorOrigin?: PopoverProps["anchorOrigin"];
  transformOrigin?: PopoverProps["transformOrigin"];
  timeout?: number;
  variant?: AlertProps["variant"];
  onAfterClose?: () => void;
};

const anchorAtom = atom<undefined | HTMLElement>({
  key: "AlertPop/Atom/anchor",
  default: undefined,
});

const propsAtom = atom<null | TProps>({
  key: "AlertPop/Atom/props",
  default: null,
});

const openAtom = atom<boolean>({
  key: "AlertPop/Atom/open",
  default: false,
});

// [ToDo] useAlertPop.setAlert() の利用箇所で、anchorEl 用の event を any にしている箇所が有る。
// ちゃんと型定義したければ、RGLReloadSharedPageButton.tsx の実装を参照。
// 又は、ChatGPT 参照 : https://chatgpt.com/c/68395065-9748-800c-9989-f06ec5fbf402
// ↓の alertError() も同様。
export const useAlertPop = () => {
  const setProps = useSetRecoilState(propsAtom);
  const setAnchor = useSetRecoilState(anchorAtom);
  const setOpen = useSetRecoilState(openAtom);
  const setAlert = useCallback(
    (props: TProps) => {
      setProps(props);
      setAnchor(props.anchor);
      setOpen(true);
    },
    [setAnchor, setOpen, setProps],
  );

  const alertError = useCallback(
    (message: string, event: any) => {
      setAlert({
        message,
        severity: "error",
        anchor: event.nativeEvent.submitter,
      });
    },
    [setAlert],
  );

  return { setAlert, alertError };
};

export const AlertPop = () => {
  const [anchor, setAnchor] = useRecoilState(anchorAtom);
  const [open, setOpen] = useRecoilState(openAtom);
  const props = useRecoilValue(propsAtom);

  const handleOnClose = useCallback(() => {
    setOpen(false);
    setAnchor(undefined);
  }, [setAnchor, setOpen]);

  useEffect(() => {
    if (open) {
      const timer = setTimeout(
        () => {
          handleOnClose();
        },
        props?.timeout ? props.timeout : 5000,
      );
      return () => clearTimeout(timer);
    }
  }, [handleOnClose, open, props]);

  if (!props) return <></>;
  const { message, severity, anchorOrigin, transformOrigin, variant } = props;

  return (
    <Popover
      open={open}
      anchorEl={anchor}
      onClose={handleOnClose}
      anchorOrigin={anchorOrigin ? anchorOrigin : { vertical: "top", horizontal: "left" }}
      transformOrigin={transformOrigin ? transformOrigin : { vertical: "bottom", horizontal: "right" }}
      disableScrollLock
      TransitionProps={{
        onExited: () => {
          props?.onAfterClose && props.onAfterClose();
        },
      }}
    >
      <Alert variant={variant ? variant : "filled"} severity={severity ? severity : "success"} sx={{ px: 1, py: 0 }}>
        {message}
      </Alert>
    </Popover>
  );
};
